import { getAllCategories, getCategoryBySlugOrId, Category } from '@/admin/utils/categoryService';
import { getAllCategoriesWithCache, getCategoryBySlugOrIdWithCache } from '@/services/categoryService';

// Constants cho danh mục khóa học
export enum COURSE_CATEGORIES {
  FAMILY = 'family',
  FAMILY_EDUCATION = 'family-education',
  BUDDHISM = 'buddhism',
  WORK = 'work',
  PERSONAL_DEVELOPMENT = 'personal-development',
}

// Fallback cho trường hợp không thể truy cập database
export const FALLBACK_CATEGORIES = [
  {
    id: COURSE_CATEGORIES.FAMILY,
    name: 'Gia đình',
    description: 'Các khóa học về ứng dụng Phật pháp trong gia đình, nuôi dạy con cái, và quan hệ gia đình',
    icon: 'family',
    color: '#F59E0B',
    slug: 'gia-dinh'
  },
  {
    id: COURSE_CATEGORIES.FAMILY_EDUCATION,
    name: 'Giáo dục gia đình',
    description: 'Các khóa học về giáo dục và phát triển trong môi trường gia đình',
    icon: 'family',
    color: '#6366F1',
    slug: 'giao-duc-gia-dinh'
  },
  {
    id: COURSE_CATEGORIES.BUDDHISM,
    name: 'Phật học',
    description: 'Các khóa học về giáo lý, thực hành và triết học Phật giáo',
    icon: 'buddhism',
    color: '#3B82F6',
    slug: 'phat-hoc'
  },
  {
    id: COURSE_CATEGORIES.WORK,
    name: 'Công việc',
    description: 'Các khóa học về áp dụng chánh niệm trong công việc, lãnh đạo và cân bằng cuộc sống',
    icon: 'work',
    color: '#10B981',
    slug: 'cong-viec'
  },
  {
    id: COURSE_CATEGORIES.PERSONAL_DEVELOPMENT,
    name: 'Phát triển cá nhân',
    description: 'Các khóa học về phát triển bản thân, tìm kiếm mục đích sống và cân bằng tinh thần',
    icon: 'personal',
    color: '#8B5CF6',
    slug: 'phat-trien-ca-nhan'
  }
];

// Lấy thông tin category theo slug hoặc ID
export const getCategoryInfo = async (slugOrId: string): Promise<Category | null> => {
  // Nếu không có slugOrId, trả về null
  if (!slugOrId) {
    console.log('getCategoryInfo called with empty slugOrId');
    return null;
  }
  
  console.log(`Getting category info for: ${slugOrId}`);
  
  try {
    // Sử dụng service cache nâng cao
    const category = await getCategoryBySlugOrIdWithCache(slugOrId);
    
    // Nếu không tìm thấy, trả về fallback nếu có
    if (!category) {
      console.log(`Category not found for ${slugOrId}, trying fallback`);
      
      // Fallback: Tìm trong danh sách cứng
      const fallbackCategory = FALLBACK_CATEGORIES.find(
        cat => cat.id === slugOrId || cat.slug === slugOrId
      );
      
      if (fallbackCategory) {
        console.log(`Found fallback category for ${slugOrId}:`, fallbackCategory.name);
      } else {
        console.log(`No fallback category found for ${slugOrId}`);
      }
      
      return fallbackCategory || null;
    }
    
    console.log(`Found category for ${slugOrId}:`, category.name);
    return category;
  } catch (error) {
    console.error(`Error in getCategoryInfo for ${slugOrId}:`, error);
    
    // Nếu xảy ra lỗi, vẫn thử tìm trong danh sách fallback
    const fallbackCategory = FALLBACK_CATEGORIES.find(
      cat => cat.id === slugOrId || cat.slug === slugOrId
    );
    
    if (fallbackCategory) {
      console.log(`Found fallback category after error for ${slugOrId}:`, fallbackCategory.name);
    }
    
    return fallbackCategory || null;
  }
};

// Lấy tên category theo slug hoặc ID
export const getCategoryName = async (slugOrId: string): Promise<string> => {
  const category = await getCategoryInfo(slugOrId);
  return category ? category.name : 'Unknown Category';
};

// Lấy tất cả thông tin category
export const getAllCategoryInfo = async (): Promise<Category[]> => {
  try {
    // Sử dụng service cache nâng cao
    const categories = await getAllCategoriesWithCache();
    return categories.length > 0 ? categories : FALLBACK_CATEGORIES;
  } catch (error) {
    console.error('Error in getAllCategoryInfo:', error);
    return FALLBACK_CATEGORIES;
  }
}; 